import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { motion } from 'framer-motion';
import { Lock, ArrowLeft, CheckCircle, ShieldCheck, Container } from 'lucide-react';
import { FormikInput } from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import { authAPI } from '../../services/api';
import SEO from '../../components/shared/SEO';

// Validation
const ResetSchema = Yup.object().shape({
    password: Yup.string()
        .min(8, 'Password must be at least 8 characters')
        .required('Required'),
    confirmPassword: Yup.string()
        .oneOf([Yup.ref('password'), null], 'Passwords must match')
        .required('Required'),
});

export default function ResetPassword() {
    const { token } = useParams();
    const navigate = useNavigate();
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState('');

    const handleReset = async (values, { setSubmitting }) => {
        try {
            // API call to backend
            await authAPI.resetPassword(values, token)
            setSuccess(true);
            setTimeout(() => navigate('/login'), 3000);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to reset password');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <SEO title="Reset Protocol"
                description="Establish a new master password for your vault."
                noIndex={true} />

            <div className="min-h-screen flex items-center justify-center bg-[#050505] text-white p-6 relative overflow-hidden font-sans">

                {/* --- Background Effects --- */}
                <div className="absolute inset-0 z-0 pointer-events-none">
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-orange-600/10 rounded-full blur-[120px] opacity-50" />
                    <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808008_1px,transparent_1px),linear-gradient(to_bottom,#80808008_1px,transparent_1px)] bg-[size:32px_32px]" />
                    <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20" />
                </div>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="w-full max-w-md bg-[#0d0d0d]/80 backdrop-blur-2xl border border-white/10 p-10 rounded-[2.5rem] shadow-2xl relative z-10"
                >
                    {!success ? (
                        <>
                            <div className="flex flex-col items-center mb-10">
                                <div className="w-14 h-14 bg-gradient-to-br from-orange-500/10 to-red-600/10 rounded-2xl flex items-center justify-center border border-orange-500/20 mb-6">
                                    <Lock className="text-orange-500" size={28} />
                                </div>
                                <h2 className="text-3xl font-black tracking-tighter text-white mb-2 text-center">New Credentials</h2>
                                <p className="text-gray-500 text-sm font-medium text-center leading-relaxed">
                                    Ensure your new password is at least 8 characters long for maximum vault security.
                                </p>
                            </div>

                            {error && (
                                <motion.div
                                    initial={{ opacity: 0, height: 0 }}
                                    animate={{ opacity: 1, height: 'auto' }}
                                    className="mb-6 p-4 bg-red-500/5 border border-red-500/20 rounded-xl text-red-400 text-xs font-bold uppercase tracking-wider text-center"
                                >
                                    {error}
                                </motion.div>
                            )}

                            <Formik
                                initialValues={{ password: '', confirmPassword: '' }}
                                validationSchema={ResetSchema}
                                onSubmit={handleReset}
                            >
                                {({ isSubmitting }) => (
                                    <Form className="space-y-2">
                                        <FormikInput
                                            name="password"
                                            label="New Secure Password"
                                            type="password"
                                            placeholder="••••••••"
                                        />
                                        <FormikInput
                                            name="confirmPassword"
                                            label="Confirm New Password"
                                            type="password"
                                            placeholder="••••••••"
                                        />

                                        <Button
                                            type="submit"
                                            className="w-full h-14 mt-12 bg-white text-black hover:bg-gray-200 font-black text-lg rounded-2xl transition-all shadow-xl shadow-white/5 disabled:opacity-50"
                                            disabled={isSubmitting}
                                        >
                                            {isSubmitting ? (
                                                <div className="flex items-center gap-2">
                                                    <span className="w-4 h-4 border-2 border-black/20 border-t-black rounded-full animate-spin" />
                                                    Updating Vault...
                                                </div>
                                            ) : (
                                                'Reset Password'
                                            )}
                                        </Button>
                                    </Form>
                                )}
                            </Formik>
                        </>
                    ) : (
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            className="text-center py-4"
                        >
                            <div className="w-16 h-16 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-6 border border-green-500/20">
                                <CheckCircle className="text-green-400" size={32} />
                            </div>
                            <h2 className="text-3xl font-black tracking-tighter mb-2">Vault Updated</h2>
                            <p className="text-gray-500 text-sm mb-10 leading-relaxed font-medium">
                                Your password has been successfully reset. <br />
                                <span className="text-white italic">Redirecting you to login...</span>
                            </p>

                            <Button
                                onClick={() => navigate('/login')}
                                variant="outline"
                                className="w-full h-12 rounded-xl text-sm font-bold border-white/10 hover:bg-white/5"
                            >
                                Back to Login
                            </Button>
                        </motion.div>
                    )}

                    {!success && (
                        <div className="mt-10 pt-8 border-t border-white/5 text-center flex flex-col items-center gap-6">
                            <Link to="/login" className="group inline-flex items-center text-xs font-black uppercase tracking-[0.2em] text-gray-500 hover:text-white transition-colors">
                                <ArrowLeft size={14} className="mr-2 group-hover:-translate-x-1 transition-transform" /> Cancel Update
                            </Link>

                            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-orange-500/5 border border-orange-500/10">
                                <ShieldCheck size={14} className="text-orange-500" />
                                <span className="text-[10px] font-black uppercase tracking-widest text-orange-500/80">
                                    End-to-End Encrypted Reset
                                </span>
                            </div>
                        </div>
                    )}
                </motion.div>


            </div>
        </>
    );
}